import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Loader2 } from "lucide-react";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = ["all", "industry", "technology", "festivals", "interviews", "reviews"];

// Simple inline link converter with markdown support
const LinkedText = ({ text }) => {
  if (!text) return null;
  
  // Combined regex: markdown links [text](url) OR raw URLs
  const combinedRegex = /\[([^\]]+)\]\((https?:\/\/[^)]+)\)|(https?:\/\/[^\s<\[\]]+[^<.,:;"')\]\s])/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  
  while ((match = combinedRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    
    if (match[1] && match[2]) {
      // Markdown link: [text](url)
      parts.push(
        <a
          key={match.index}
          href={match[2]}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-purple-light hover:text-white transition-colors underline underline-offset-2"
        >
          {match[1]}
        </a>
      );
    } else if (match[3]) {
      // Raw URL
      const url = match[3];
      parts.push(
        <a
          key={match.index}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-purple-light hover:text-white transition-colors underline underline-offset-2"
        >
          {url.length > 40 ? url.slice(0, 40) + '...' : url}
        </a>
      );
    }
    
    lastIndex = match.index + match[0].length;
  }
  
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  
  if (parts.length === 0) {
    return <>{text}</>;
  }
  
  return <>{parts}</>;
};

export default function News() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const PAGE_SIZE = 24;

  useEffect(() => {
    setPage(0);
  }, [selectedCategory]);

  useEffect(() => {
    fetchNews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, page]);

  const fetchNews = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip: page * PAGE_SIZE, limit: PAGE_SIZE });
      if (selectedCategory !== "all") params.set("category", selectedCategory);
      const response = await axios.get(`${API}/news?${params}`);
      // The paginated endpoint returns { items, total, skip, limit }. Older
      // backends return a bare array — tolerate both, since the frontend
      // deploys on push while the backend is deployed separately by CLI.
      const payload = response.data;
      const isPaged = payload && !Array.isArray(payload) && Array.isArray(payload.items);
      setNews(isPaged ? payload.items : (Array.isArray(payload) ? payload : []));
      setTotal(isPaged ? (payload.total || 0) : (Array.isArray(payload) ? payload.length : 0));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      console.error("Error fetching news:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8">
      <SEO 
        title="AI Film News"
        description="Latest news and updates from the AI filmmaking world. Industry trends, technology advances, and festival coverage."
        url="https://aifilmsociety.org/news"
      />
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-purple-light mb-4 font-mono">
            Stay Informed
          </p>
          <h1 className="font-syne text-4xl md:text-6xl font-bold text-white mb-6">
            AI Film News
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl">
            The latest updates from the world of AI-generated cinema, technology breakthroughs, 
            and industry developments.
          </p>
        </motion.div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              data-testid={`filter-${category}`}
              className={`px-4 py-2 text-xs uppercase tracking-wider transition-all ${
                selectedCategory === category
                  ? "bg-purple text-white"
                  : "bg-void-surface text-slate-400 hover:text-white border border-white/10"
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* News Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-purple animate-spin" />
          </div>
        ) : news.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-slate-400">No news articles found.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {news.map((article, index) => (
              <motion.article
                key={article.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="group"
              >
                <Link to={`/news/${article.id}`} data-testid={`news-article-${index}`}>
                  <div className="aspect-video mb-4 overflow-hidden bg-void-surface">
                    <img 
                      src={article.image_url || "https://images.unsplash.com/photo-1705412893210-b15bcac44d33?crop=entropy&cs=srgb&fm=jpg&q=85"}
                      alt={article.title}
                      className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                    />
                  </div>
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xs uppercase tracking-wider text-purple-light font-mono">
                      {article.category}
                    </span>
                    <span className="text-xs text-slate-500">
                      {new Date(article.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <h2 className="font-syne font-bold text-white text-xl mb-2 group-hover:text-purple-light transition-colors">
                    {article.title}
                  </h2>
                  <p className="text-slate-400 line-clamp-2">
                    <LinkedText text={article.excerpt} />
                  </p>
                  <p className="text-sm text-slate-500 mt-3">By {article.author}</p>
                </Link>
              </motion.article>
            ))}
          </div>
        )}

        {total > PAGE_SIZE && !loading && (
          <div className="flex items-center justify-center gap-6 mt-16">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              data-testid="news-prev"
              className="px-5 py-2 border border-white/10 text-sm text-slate-300 hover:border-purple/40 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <span className="text-sm text-slate-500 font-mono">
              {page + 1} / {Math.ceil(total / PAGE_SIZE)}
            </span>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={(page + 1) * PAGE_SIZE >= total}
              data-testid="news-next"
              className="px-5 py-2 border border-white/10 text-sm text-slate-300 hover:border-purple/40 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
